import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SETTING_DEFS, LEGAL_PAGES, type LegalPageSlug } from "@gaole/shared";
import { api } from "../lib/api";
import { toast } from "../stores/toast";
import { useSettingsDraft } from "../hooks/useSettingsDraft";
import {
  GROUP_LABELS,
  isSettingsTabVisible,
  settingsSlugToTab,
  settingsTabLabel,
  settingsTabPath,
  TAB_GROUPS,
  type SettingsTab,
} from "../config/admin-settings";
import {
  Button,
  Input,
  PageLoader,
  PillSegmentSwitcher,
  SearchSelect,
  SettingsCard,
  Textarea,
  Toggle,
  useConfirm,
} from "../components/ui";
import { DataTable, type Column, type DataTableRowActions } from "../components/DataTable";
import { CreditPackagesEditor } from "./settings/CreditPackagesEditor";
import { CreditsSettingsEditor } from "./settings/CreditsSettingsEditor";
import { SecuritySettingsEditor } from "./settings/SecuritySettingsEditor";
import { CompanySettingsEditor } from "./settings/CompanySettingsEditor";
import { EmailTemplatesEditor } from "./settings/EmailTemplatesEditor";
import { OrderNumberSettingsEditor } from "./settings/OrderNumberSettingsEditor";
import { ADMIN_HUB_PAGE } from "../components/shell/adminHubPage";
import { MarkdownEditor } from "../components/MarkdownEditor";
import { ImageUploadField } from "../components/ImageUploadField";
import { useAuth } from "../stores/auth";

interface SettingRow {
  key: string;
  group: string;
  label: string;
  type: string;
  value: unknown;
  description?: string;
}

interface Currency {
  code: string;
  name: string;
  symbol: string;
  rateToBase: number;
  enabled: boolean;
  isBase: boolean;
}

interface VisionModelOption {
  id: string;
  name: string;
  modelId: string;
  isDefault: boolean;
}

type AiModelOption = VisionModelOption;

function formatJsonSettingValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function JsonSettingField({
  row,
  value,
  onChange,
  hint,
}: {
  row: SettingRow;
  value: unknown;
  onChange: (v: unknown) => void;
  hint?: string;
}) {
  const [text, setText] = useState(() => formatJsonSettingValue(value));
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setText(formatJsonSettingValue(value));
    setParseError(null);
  }, [value, row.key]);

  return (
    <Textarea
      label={row.label}
      rows={8}
      className="font-mono text-xs leading-relaxed"
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        if (!next.trim()) {
          setParseError("JSON cannot be empty");
          return;
        }
        try {
          onChange(JSON.parse(next));
          setParseError(null);
        } catch {
          setParseError("Invalid JSON — fix before saving");
        }
      }}
      hint={parseError ?? hint}
    />
  );
}

export function SettingField({
  row,
  value,
  onChange,
  visionModels,
  imageGenModels,
}: {
  row: SettingRow;
  value: unknown;
  onChange: (v: unknown) => void;
  visionModels?: AiModelOption[];
  imageGenModels?: AiModelOption[];
}) {
  const hint = row.description;

  if (row.key === "carduploader.model_id" && visionModels) {
    const options = [
      { value: "", label: "Default Vision model", hint: "Uses the default from Models & Knowledge" },
      ...visionModels.map((m) => ({
        value: m.id,
        label: m.name,
        hint: `${m.modelId}${m.isDefault ? " · default" : ""}`,
      })),
    ];
    return (
      <SearchSelect
        label={row.label}
        hint={hint ?? "Models are configured under Admin → Models & Knowledge (Vision purpose)."}
        placeholder="Select a vision model…"
        options={options}
        value={String(value ?? "")}
        onChange={(v) => onChange(v)}
        emptyMessage="No Vision models configured — add one under Models & Knowledge."
      />
    );
  }

  if (row.key === "packs.artwork_model_id" && imageGenModels) {
    const options = [
      { value: "", label: "Default Image Gen model", hint: "Uses the default from Models & Knowledge" },
      ...imageGenModels.map((m) => ({
        value: m.id,
        label: m.name,
        hint: `${m.modelId}${m.isDefault ? " · default" : ""}`,
      })),
    ];
    return (
      <SearchSelect
        label={row.label}
        hint={hint ?? "Models are configured under Admin → Models & Knowledge (Image generation purpose)."}
        placeholder="Select an image model…"
        options={options}
        value={String(value ?? "")}
        onChange={(v) => onChange(v)}
        emptyMessage="No Image Gen models configured — add one under Models & Knowledge."
      />
    );
  }

  if (row.key === "orders.number_template") {
    return <OrderNumberSettingsEditor value={value} onChange={onChange} />;
  }

  if (row.key === "payments.bank_in.qr_image_url") {
    const urls = String(value ?? "").trim() ? [String(value)] : [];
    return (
      <ImageUploadField
        label={row.label}
        hint={hint ?? "PNG or JPG. Shown to customers during manual bank transfer."}
        value={urls}
        onChange={(next) => onChange(next[0] ?? "")}
        max={1}
      />
    );
  }

  if (row.type === "boolean") {
    return (
      <Toggle checked={Boolean(value)} onChange={(v) => onChange(v)} label={row.label} />
    );
  }

  if (row.type === "secret") {
    return (
      <Input
        label={row.label}
        type="password"
        autoComplete="off"
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        hint={hint ?? "Leave as •••••••• to keep the current value"}
      />
    );
  }

  if (row.type === "number") {
    return (
      <Input
        label={row.label}
        type="number"
        step="any"
        value={value === "" || value == null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
        hint={hint}
      />
    );
  }

  if (row.type === "json") {
    return <JsonSettingField row={row} value={value} onChange={onChange} hint={hint} />;
  }

  const isLong =
    row.key.includes("instructions") ||
    row.key.includes("template") ||
    String(value ?? "").length > 80;

  if (isLong) {
    return (
      <Textarea
        label={row.label}
        rows={3}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <Input
      label={row.label}
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
      hint={hint}
    />
  );
}

function CurrencyEditor({
  currencies,
  onSaved,
}: {
  currencies: Currency[];
  onSaved: () => void;
}) {
  const { confirm, dialog } = useConfirm();
  const [draft, setDraft] = useState<{
    code: string;
    name: string;
    symbol: string;
    rateToBase: string;
    enabled: boolean;
  }>({
    code: "",
    name: "",
    symbol: "",
    rateToBase: "1",
    enabled: true,
  });

  const saveMut = useMutation({
    mutationFn: (c: typeof draft) =>
      api(`/settings/currencies/${c.code}`, {
        method: "PUT",
        body: {
          code: c.code,
          name: c.name,
          symbol: c.symbol,
          rateToBase: Number(draft.rateToBase) || 1,
          enabled: c.enabled ?? true,
        },
      }),
    onSuccess: () => {
      toast("success", "Currency saved");
      setDraft({ code: "", name: "", symbol: "", rateToBase: "1", enabled: true });
      onSaved();
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (code: string) => api(`/settings/currencies/${code}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Currency removed");
      onSaved();
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const columns: Column<Currency>[] = [
    {
      key: "code",
      header: "Code",
      sortable: true,
      sortValue: (r) => r.code,
      render: (r) => <span className="font-mono font-semibold">{r.code}</span>,
    },
    {
      key: "name",
      header: "Name",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => r.name,
    },
    {
      key: "symbol",
      header: "Symbol",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.symbol,
      render: (r) => r.symbol,
    },
    {
      key: "rate",
      header: "Rate to base",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.rateToBase,
      render: (r) => <span className="tabular-nums">{r.rateToBase}</span>,
    },
    {
      key: "enabled",
      header: "Status",
      sortable: true,
      sortValue: (r) => (r.isBase ? "Base" : r.enabled ? "Enabled" : "Disabled"),
      exportValue: (r) => (r.isBase ? "Base" : r.enabled ? "Enabled" : "Disabled"),
      render: (r) => (r.isBase ? "Base" : r.enabled ? "Enabled" : "Disabled"),
    },
  ];

  const rowActions = useMemo((): DataTableRowActions<Currency> => ({
    extra: (r) => {
      if (r.isBase) return [];
      return [
        {
          label: "Edit",
          onClick: () => setDraft({ ...r, rateToBase: String(r.rateToBase) }),
        },
        {
          label: "Delete",
          variant: "danger",
          onClick: () =>
            confirm({
              title: `Delete currency ${r.code}?`,
              danger: true,
              confirmLabel: "Delete",
              action: () => deleteMut.mutateAsync(r.code),
            }),
        },
      ];
    },
  }), [confirm, deleteMut]);

  return (
    <>
      {dialog}
      <SettingsCard
      title="Currencies"
      description="Exchange rates relative to the base currency (general.base_currency)."
      footer={
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!draft.code || !draft.name || saveMut.isPending}
            onClick={() => saveMut.mutate(draft)}
          >
            Save currency
          </Button>
        </div>
      }
    >
      <div className="form-row-2">
        <Input
          label="Code"
          value={draft.code}
          onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
          placeholder="USD"
        />
        <Input
          label="Name"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <Input
          label="Symbol"
          value={draft.symbol}
          onChange={(e) => setDraft((d) => ({ ...d, symbol: e.target.value }))}
        />
        <Input
          label="Rate to base"
          type="number"
          step="any"
          value={draft.rateToBase}
          onChange={(e) => setDraft((d) => ({ ...d, rateToBase: e.target.value }))}
        />
      </div>
      <Toggle
        checked={draft.enabled ?? true}
        onChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
        label="Enabled for checkout"
      />
      <DataTable
        columns={columns}
        rows={currencies}
        rowKey={(r) => r.code}
        exportFilename="currencies"
        rowActions={rowActions}
      />
    </SettingsCard>
    </>
  );
}

function LegalSettingsEditor({
  draft,
  setValue,
  saveMut,
}: {
  draft: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  saveMut: { mutate: (keys: string[]) => void; isPending: boolean };
}) {
  const [legalSlug, setLegalSlug] = useState<LegalPageSlug>("terms");
  const activePage = LEGAL_PAGES.find((p) => p.slug === legalSlug) ?? LEGAL_PAGES[0];
  const settingKey = activePage.settingKey;
  const value = String(draft[settingKey] ?? "");

  return (
    <div className={ADMIN_HUB_PAGE}>
      <div>
        <h1 className="font-display text-lg font-bold">{settingsTabLabel("legal")}</h1>
        <p className="mt-0.5 text-xs text-muted">
          Markdown content shown on the storefront at /legal/{activePage.slug}.
        </p>
      </div>
      <PillSegmentSwitcher
        value={legalSlug}
        onChange={setLegalSlug}
        options={LEGAL_PAGES.map((p) => ({ value: p.slug, label: p.title }))}
      />
      <SettingsCard
        title={activePage.title}
        description="Use Markdown for headings, lists, and links. Save to publish on the storefront."
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate([settingKey])}>
              Save
            </Button>
          </div>
        }
      >
        <MarkdownEditor
          value={value}
          onChange={(v) => setValue(settingKey, v)}
          hint="Customers must accept Terms & Privacy at registration and all policies at checkout."
        />
      </SettingsCard>
    </div>
  );
}

export function Settings() {
  const { section } = useParams<{ section: string }>();
  const { moduleEnabled } = useAuth();
  const tab = settingsSlugToTab(section);
  const activeTab: SettingsTab = tab ?? "general";

  if (tab && !isSettingsTabVisible(activeTab, moduleEnabled)) {
    return <Navigate to={settingsTabPath("general")} replace />;
  }

  const { draft, setValue, saveMut, settings: data, showLoader } = useSettingsDraft();
  const [smtpTestTo, setSmtpTestTo] = useState("");

  const { data: currencies, refetch: refetchCurrencies, isLoading: currenciesLoading } = useQuery({
    queryKey: ["settings", "currencies"],
    queryFn: () => api<{ currencies: Currency[] }>("/settings/currencies").then((r) => r.currencies),
    enabled: activeTab === "currencies",
  });

  const { data: visionModels } = useQuery({
    queryKey: ["settings", "vision-models"],
    queryFn: () => api<{ items: VisionModelOption[] }>("/settings/vision-models").then((r) => r.items),
    enabled: activeTab === "carduploader",
  });

  const { data: imageGenModels } = useQuery({
    queryKey: ["settings", "image-gen-models"],
    queryFn: () => api<{ items: VisionModelOption[] }>("/settings/image-gen-models").then((r) => r.items),
    enabled: activeTab === "raffles_auctions",
  });

  const smtpTestMut = useMutation({
    mutationFn: (to: string) => api("/settings/smtp-test", { method: "POST", body: { to } }),
    onSuccess: () => toast("success", "Test email sent"),
    onError: (e: Error) => toast("error", e.message),
  });

  const activeGroups = TAB_GROUPS[activeTab];

  if (!tab) {
    return <Navigate to="/admin/settings/general" replace />;
  }

  if (activeTab === "currencies") {
    if (currenciesLoading && !currencies) return <PageLoader />;
    return (
      <div className={ADMIN_HUB_PAGE}>
        {currencies && <CurrencyEditor currencies={currencies} onSaved={() => void refetchCurrencies()} />}
      </div>
    );
  }

  if (activeTab === "credits") {
    if (showLoader) return <PageLoader />;
    return (
      <div className="page-scroll space-y-5">
        <CreditsSettingsEditor draft={draft} setValue={setValue} saveMut={saveMut} />
        <div className="w-full min-w-0">
          <SettingsCard title="Credit packages" description="Top-up offers shown on the customer Wallet page — not in the Shop catalog.">
            <CreditPackagesEditor />
          </SettingsCard>
        </div>
      </div>
    );
  }

  if (activeTab === "email_templates") {
    if (showLoader) return <PageLoader />;
    const smtpEnabled = Boolean(draft["smtp.enabled"] ?? data?.find((s) => s.key === "smtp.enabled")?.value);
    return (
      <div className="page-scroll space-y-5">
        <EmailTemplatesEditor
          draft={draft}
          setValue={setValue}
          saveMut={saveMut}
          smtpEnabled={smtpEnabled}
        />
      </div>
    );
  }

  if (activeTab === "legal") {
    if (showLoader) return <PageLoader />;
    return <LegalSettingsEditor draft={draft} setValue={setValue} saveMut={saveMut} />;
  }

  if (activeTab === "company") {
    if (showLoader) return <PageLoader />;
    return <CompanySettingsEditor draft={draft} setValue={setValue} saveMut={saveMut} />;
  }

  if (activeTab === "security") {
    if (showLoader) return <PageLoader />;
    const smtpEnabled = Boolean(draft["smtp.enabled"] ?? data?.find((s) => s.key === "smtp.enabled")?.value);
    return (
      <SecuritySettingsEditor
        draft={draft}
        setValue={setValue}
        saveMut={saveMut}
        smtpEnabled={smtpEnabled}
      />
    );
  }

  if (showLoader) return <PageLoader />;

  return (
    <div className={ADMIN_HUB_PAGE}>
      <div>
        <h1 className="font-display text-lg font-bold">{settingsTabLabel(activeTab)}</h1>
        <p className="mt-0.5 text-xs text-muted">Store configuration, gateways, and operational defaults.</p>
      </div>

      {activeGroups.map((groupKey) => {
        const defs = SETTING_DEFS.filter((d) => d.group === groupKey);
        const keys = defs.map((d) => d.key);
        return (
          <SettingsCard
            key={groupKey}
            title={GROUP_LABELS[groupKey] ?? groupKey}
            footer={
              <div className="flex justify-end">
                <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate(keys)}>
                  Save
                </Button>
              </div>
            }
          >
            <div className="space-y-4">
              {defs.map((def) => {
                const row = (data ?? []).find((s) => s.key === def.key) ?? {
                  ...def,
                  value: draft[def.key] ?? def.defaultValue,
                };
                return (
                  <SettingField
                    key={def.key}
                    row={row}
                    value={draft[def.key] ?? row.value}
                    onChange={(v) => setValue(def.key, v)}
                    visionModels={visionModels}
                    imageGenModels={imageGenModels}
                  />
                );
              })}
            </div>
          </SettingsCard>
        );
      })}

      {activeTab === "smtp" && (
        <SettingsCard title="Send test email" description="Verify SMTP credentials with a one-off message.">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <Input
                label="Recipient"
                type="email"
                value={smtpTestTo}
                onChange={(e) => setSmtpTestTo(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!smtpTestTo || smtpTestMut.isPending}
              onClick={() => smtpTestMut.mutate(smtpTestTo)}
            >
              Send test
            </Button>
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
